import React, { useEffect, useState } from 'react';
import { object, bool, string } from 'prop-types';
import { checkLinkAndAppendLanguage, generateNewLanguageUrl } from '../../utils/localization';
import { ServerCommunicationUtil } from '../../utils/server_communication_util';
import { initializeOptimizelyWithKey } from '../../utils/optimizely_helpers';
import NavigatorModal from '../NavigatorModal';
import logError from '../../utils/logError';
import useMobileWidth from '../../hooks/useMobileWidth';
import { getAmplitudeInstance } from '../../utils/amplitude_helpers';
import { APPLE, GOOGLE_ONE_TAP } from '../../utils/constants/LoginType';
import { ONE_TAP_DISPLAYED, SIGN_UP_SUCCEEDED } from '../../utils/constants/AmplitudeEvent';
import { ENGLISH } from '../../utils/constants/language_region_codes';

const GlobalActions = ({ currentUser, proUser, optimizelyKey, languageRegionCode, userLang, branchKey, mobileBrowser, googleClientId }) => {
  const [children, setChildren] = useState([]);
  const isMobileWidth = useMobileWidth();

  const setUpTatari = () => {
    if (currentUser && window.tatari) {
      // Safety first - Wrap anything Tatari-related in a try/catch
      try {
        window.tatari.identify(currentUser.id);
      } catch (e) {
        logError(e);
      }
    }
  };

  // In testing the pendo script tag has been slow to load so we
  // attempt up to 4 times every 750 ms to initialize the user.
  let pendoSetUpAttempts = 0;
  const maxPendoSetUpAttempts = 3;
  const pendoSetUpBackOff = 750;

  const setUpPendo = () => {
    if (!currentUser || pendoSetUpAttempts > maxPendoSetUpAttempts) {
      return;
    }

    if (window.pendo) {
      try {
        const visitor = {
          id: currentUser.id,
          pro: proUser
        };
        window.pendo.initialize({ visitor });
      } catch (e) {
        logError(e);
      }
    } else {
      pendoSetUpAttempts += 1;
      setTimeout(setUpPendo, pendoSetUpBackOff);
    }
  };

  const setUpOptimizely = () => {
    try {
      if (optimizelyKey) {
        initializeOptimizelyWithKey(optimizelyKey);
      }
    } catch (e) {
      logError(e);
    }
  };

  const getCurrentLanguage = () => {
    let language = '/en';
    if (languageRegionCode) {
      const path_language_regex = languageRegionCode.substring(0, 2).match(/(es|fr|de)/);
      if (Array.isArray(path_language_regex)) {
        language = `/${path_language_regex[0]}`;
      }
    }
    return language;
  };

  const redirectToProperLanguage = newLanguage => {
    if (window) {
      window.location = generateNewLanguageUrl(window.location.pathname, newLanguage);
    }
  };

  const showNavigatorModal = () => {
    if (!branchKey) {
      return;
    }

    const navigatorMessageParam = 'navigator_desktop';
    const searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.has(navigatorMessageParam)) {
      return;
    }

    setChildren([...children, <NavigatorModal branchKey={branchKey} languageRegionCode={languageRegionCode} mobileBrowser={mobileBrowser} />]);
  };

  useEffect(() => {
    // TODO: Move this redirect logic to the controller
    const currentLanguage = getCurrentLanguage();

    if (currentLanguage && userLang && currentLanguage !== userLang) {
      redirectToProperLanguage(userLang);
    }
  }, []);

  useEffect(() => {
    setUpTatari();
  }, []);

  useEffect(() => {
    setUpPendo();
  }, []);

  useEffect(() => {
    setUpOptimizely();
  }, []);

  useEffect(() => {
    showNavigatorModal();
  }, []);

  useEffect(() => {
    function callback(resp) {
      const data = { google_token: resp.credential, create_session: true, one_tap: true };
      const headers = { 'X-Language-Locale': languageRegionCode };
      ServerCommunicationUtil.postApiEndpoint(
        '/api/alltrails/google/login',
        data,
        response => {
          if (response.is_sign_up) {
            getAmplitudeInstance().logEvent(SIGN_UP_SUCCEEDED, { 'login type': GOOGLE_ONE_TAP });
          }
          if (response.welcome_notice) {
            const params = new URLSearchParams(window.location.search);
            params.set('welcome', response.welcome_notice);
            const search = params.toString();
            window.location.replace(window.location.href + (search !== '' ? `?${search}` : ''));
          } else {
            window.location.replace(window.location.href);
          }
        },
        e => {
          logError(e);
        },
        () => {},
        headers
      );
    }

    const setUpGoogleOneTap = () => {
      window.google.accounts.id.initialize({
        cancel_on_tap_outside: false,
        client_id: googleClientId,
        callback,
        prompt_parent_id: 'g_one_tap'
      });

      window.google.accounts.id.prompt(notification => {
        if (notification.isDisplayMoment() && notification.isDisplayed()) {
          getAmplitudeInstance().logEvent(ONE_TAP_DISPLAYED);
        }
      });
    };

    if (!currentUser && !isMobileWidth && !['/signup', '/login'].some(path => checkLinkAndAppendLanguage(path) === window.location.pathname)) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setUpGoogleOneTap();
      document.body.appendChild(script);
    }
  }, [currentUser, googleClientId, isMobileWidth, languageRegionCode]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('login') === APPLE) {
      getAmplitudeInstance().logEvent(SIGN_UP_SUCCEEDED, { 'login type': APPLE }, () => {
        const search = params.toString();
        window.history.replaceState(null, null, window.location.pathname + (search !== '' ? `?${search}` : ''));
      });
    }
  }, []);

  return <>{children}</>;
};

GlobalActions.propTypes = {
  branchKey: string,
  currentUser: object,
  languageRegionCode: string,
  proUser: bool,
  userLang: string,
  googleClientId: string.isRequired
};

GlobalActions.defaultProps = {
  branchKey: null,
  currentUser: null,
  languageRegionCode: ENGLISH,
  proUser: null,
  userLang: '/en'
};

export default GlobalActions;
